// src/pages/ChatBot/configs/api.ts
//
// Thin client for the tenant-config admin API (mounted at /chatbot/config).
// Auth rides on cookies (access_token + id_token) like the rest of the admin
// console — every call sends credentials: 'include'. Errors surface as
// ConfigApiError carrying the HTTP status; a 422 (validation failure, in which
// case the backend persisted NOTHING) additionally carries the rejected list so
// the panel can attach each reason under its control. Mirrors the idiom of
// ../query-logs/api.ts.

import type {
  ConfigSurface,
  RejectedOverride,
  SaveConfigResult,
} from './types';

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/chatbot/config`;

export class ConfigApiError extends Error {
  status: number;
  // Only set for a 422 all-or-nothing rejection.
  rejected?: RejectedOverride[];

  constructor(message: string, status: number, rejected?: RejectedOverride[]) {
    super(message);
    this.name = 'ConfigApiError';
    this.status = status;
    if (rejected) this.rejected = rejected;
  }
}

// Pull the server's { error } message off a failed response, falling back to a
// generic status message when the body isn't the expected JSON shape.
async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.error === 'string') return body.error;
  } catch {
    // non-JSON error body — keep the fallback
  }
  return fallback;
}

export async function fetchConfigSurface(): Promise<ConfigSurface> {
  const res = await fetch(BASE_URL, { credentials: 'include' });
  if (!res.ok) {
    throw new ConfigApiError(
      await readErrorMessage(res, `Failed to load configuration (${res.status})`),
      res.status,
    );
  }
  return res.json() as Promise<ConfigSurface>;
}

export async function saveConfigOverrides(
  overrides: Record<string, unknown>,
  resetIds: string[],
): Promise<SaveConfigResult> {
  const res = await fetch(BASE_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ overrides, resetIds }),
  });

  // 422 = validation failure: nothing was persisted. Carry the rejected list so
  // the caller can flag each offending control inline.
  if (res.status === 422) {
    let rejected: RejectedOverride[] = [];
    try {
      const body = await res.json();
      if (Array.isArray(body?.rejected)) rejected = body.rejected;
    } catch {
      // non-JSON 422 — leave the list empty; the banner still shows
    }
    throw new ConfigApiError('Some settings were rejected.', 422, rejected);
  }

  if (!res.ok) {
    throw new ConfigApiError(
      await readErrorMessage(res, `Failed to save configuration (${res.status})`),
      res.status,
    );
  }

  return res.json() as Promise<SaveConfigResult>;
}
